import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

import { CatalogRoom } from '~/types/stopsell';

interface Props {
  open: boolean;
  onClose: () => void;
  onContinue: (selectedRates: Record<string, Array<string>>) => void;
  catalogRooms: Array<CatalogRoom>;
  fileName: string;
}

function initAllSelected(rooms: Array<CatalogRoom>): Record<string, Set<string>> {
  return rooms.reduce<Record<string, Set<string>>>((acc, room) => {
    acc[room.id] = new Set(room.roomRates.map((r) => r.id));
    return acc;
  }, {});
}

export default function StopsellRateSelectModal({ open, onClose, onContinue, catalogRooms, fileName }: Props) {
  const [selected, setSelected] = useState<Record<string, Set<string>>>(() => initAllSelected(catalogRooms));
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelected(initAllSelected(catalogRooms));
    }
    prevOpenRef.current = open;
  }, [open, catalogRooms]);

  const totalRates = useMemo(() => catalogRooms.reduce((sum, room) => sum + room.roomRates.length, 0), [catalogRooms]);

  const selectedCount = useMemo(() => Object.values(selected).reduce((sum, set) => sum + set.size, 0), [selected]);

  const handleRateToggle = useCallback((roomId: string, rateId: string) => {
    setSelected((prev) => {
      const next = new Set(prev[roomId]);
      if (next.has(rateId)) {
        next.delete(rateId);
      } else {
        next.add(rateId);
      }
      return { ...prev, [roomId]: next };
    });
  }, []);

  const handleRoomToggleAll = useCallback((roomId: string, rateIds: Array<string>, checked: boolean) => {
    setSelected((prev) => ({ ...prev, [roomId]: checked ? new Set(rateIds) : new Set() }));
  }, []);

  const handleContinue = useCallback(() => {
    const result = Object.fromEntries(
      Object.entries(selected).map(([roomId, rateSet]) => [roomId, Array.from(rateSet)]),
    );
    onContinue(result);
  }, [selected, onContinue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <TuneIcon color="primary" />
          <Typography variant="h6">Select Room Rates</Typography>
        </Stack>
        <Chip icon={<DescriptionOutlinedIcon />} label={fileName} size="small" sx={{ mt: 1 }} />
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose which room rates to apply stopsell dates to. This applies to both extracted and manually added rooms.
        </Typography>

        <Stack gap={2}>
          {catalogRooms.map((room, index) => {
            const roomSelected = selected[room.id] ?? new Set<string>();
            const rateIds = room.roomRates.map((r) => r.id);
            const allChecked = rateIds.length > 0 && roomSelected.size === rateIds.length;
            const anyChecked = roomSelected.size > 0;

            return (
              <Box key={room.id}>
                {index > 0 && <Divider sx={{ mb: 2 }} />}

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">{room.name}</Typography>
                  {rateIds.length > 0 && (
                    <FormControlLabel
                      label={<Typography variant="caption">Select all</Typography>}
                      labelPlacement="start"
                      sx={{ mr: 0, gap: 0.5 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={allChecked}
                          indeterminate={anyChecked && !allChecked}
                          onChange={(e) => handleRoomToggleAll(room.id, rateIds, e.target.checked)}
                          sx={{ p: 0.25 }}
                        />
                      }
                    />
                  )}
                </Stack>

                {rateIds.length === 0 ? (
                  <Typography variant="caption" color="text.disabled">
                    No rates configured for this room
                  </Typography>
                ) : (
                  <Stack gap={0.5}>
                    {room.roomRates.map((rate) => (
                      <Box
                        key={rate.id}
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <FormControlLabel
                          label={<Typography variant="body2">{rate.name}</Typography>}
                          sx={{ m: 0, width: '100%' }}
                          control={
                            <Checkbox
                              size="small"
                              checked={roomSelected.has(rate.id)}
                              onChange={() => handleRateToggle(room.id, rate.id)}
                              sx={{ p: 0.25, mr: 0.5 }}
                            />
                          }
                        />
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {selectedCount} of {totalRates} rate{totalRates !== 1 ? 's' : ''} selected
        </Typography>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleContinue} disabled={selectedCount === 0}>
            Continue to Review
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
